import { getWarrantyPublicOfferViewRoute, putWarrantyPublicOfferRoute } from "@slfinrtl/ui-bff-routes"
import type { ActionItem, WarrantyPublicOfferView } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useState } from "react"
import { useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { porscheApprovedMessages } from "src/common/messages/porscheApproved.messages"
import { AnimatedStage } from "src/components"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useBff, useBffDynamic } from "src/hooks"
import { DisablementReasonList } from "src/screens/common/porscheApproved/DisablementReasonList"
import { useDisablementReasons } from "src/screens/common/porscheApproved/useDisablementReasons"
import {
	useVehicleDetailsContext,
	useVehicleDetailsContextForStage,
} from "src/screens/vehicleDetails/context/VehicleDetailsContext"
import { useEditActionButton } from "src/screens/vehicleDetails/hooks/useEditActionButton"
import { PorscheApprovedVehicleEditStage } from "./components/PorscheApprovedVehicleEditStage"

export const PorscheApprovedVehicleEditContainer = withErrorHandler(
	({ stageEditAction }: { stageEditAction?: ActionItem }) => {
		const { formatMessage } = useIntl()
		const [view, setView] = useState<WarrantyPublicOfferView>()
		const [mileage, setMileage] = useState("")

		const { inventoryId, mode, setEditStage } = useVehicleDetailsContextForStage("porscheApprovedPublicOffer")
		const { subscribe } = useVehicleDetailsContext()

		const fetchView = useBff(
			getWarrantyPublicOfferViewRoute,
			{
				body: undefined,
				pathParams: { inventoryId },
				queryParams: {},
			},
			"inline",
		)
		const putWarrantyPublicOffer = useBffDynamic(putWarrantyPublicOfferRoute, "inline")
		const onBffResponse = useCallback(
			(viewResponse: WarrantyPublicOfferView) => {
				setView(viewResponse)
			},
			[setView],
		)
		const saveDealerDecision = useCallback(
			async (approved: boolean) => {
				const updatedView = await putWarrantyPublicOffer({
					body: { dealerDecisionPorscheApproved: approved },
					pathParams: { inventoryId },
					queryParams: {},
				})
				setView(updatedView)
			},
			[putWarrantyPublicOffer, inventoryId],
		)

		useEffect(() => {
			subscribe("inventoryInformationUpdated", (values) => {
				if (values && mileage !== values.conditionAndService.mileage) {
					setMileage(values.conditionAndService.mileage)
				}
			})
		}, [subscribe, mileage, setMileage])
		useEffect(() => {
			fetchView().then(onBffResponse)
		}, [fetchView, onBffResponse, mileage])

		const disablementReasons = useDisablementReasons(view)
		const actionButton = useEditActionButton(
			"porscheApprovedPublicOffer",
			stageEditAction && {
				...stageEditAction,
				disabledHint:
					stageEditAction?.disabledHint ??
					(disablementReasons.length > 0 && (
						<DisablementReasonList
							disablementLabel={porscheApprovedMessages.violationReasonsLabel}
							disablementReasons={disablementReasons}
						/>
					)),
			},
			undefined,
		)

		return (
			<AnimatedStage title={formatMessage(commonMessages.porscheApproved)} actionButtons={view && actionButton}>
				<PorscheApprovedVehicleEditStage
					mode={mode}
					saveDealerDecision={saveDealerDecision}
					setEditStage={setEditStage}
					view={view}
				/>
			</AnimatedStage>
		)
	},
)
